import * as React from "react";
import {
    Button,
    createStyles,
    Grid,
    TextField,
    Theme,
    withStyles,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@material-ui/core";
import { API } from "aws-amplify";
import {LoadingSpinner} from "../../../utils/LoadingSpinner";

const styles = ({ spacing }: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            marginLeft: spacing(16),
            marginRight: spacing(16),
        },
        formControl: {
            margin: spacing(1),
            minWidth: 200,
        },
        textField: {
            margin: spacing(1),
            width: "100%",
        },
        successMessage: {
            padding: spacing(2),
            marginTop: spacing(2),
            fontSize: '16px'
        }
    });

enum HubsProgram {
    ASSEMBIA = "ASSEMBIA",
    ASSISTRX = "ASSISTRX",
    EVERSANA = "EVERSANA"
}

interface ICreateHubsPatientState {
    program: HubsProgram | '';
    putPatientRequest: string;
    formSubmitted: boolean;
    resultSuccessful: boolean;
    partnerPatientId: string;
}

class CreateHubsPatientSection extends React.Component<any, ICreateHubsPatientState> {
    constructor(props: any) {
        super(props);
        this.state = {
            program: '',
            putPatientRequest: '',
            formSubmitted: false,
            resultSuccessful: false,
            partnerPatientId: ''
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleValidation = this.handleValidation.bind(this);
    }

    public componentDidMount() {
        this.setState({ putPatientRequest: this.getSampleJSON() });
    }

    private getSampleJSON(): string {
        return JSON.stringify({
            "partnerPatientId": "",
            "patientDetails": {
                "email": "",
                "contactInfo": [{
                    "countryCode": 1,
                    "phoneNumber": "",
                    "contactSystemType": "Mobile"
                }],
                "smsConsent": true,
                "name": {
                    "firstName": "",
                    "lastName": "",
                    "middleName": null,
                    "suffix": null,
                    "prefix": null
                },
                "dateOfBirth": "",
                "allergyDetails": {
                    "noKnownAllergies": false,
                    "allergies": [{
                        "code": "",
                        "codeType": "",
                        "textValue": ""
                    }]
                },
                "existingMedicalConditionDetails": {
                    "noKnownExistingMedicalConditions": false,
                    "existingMedicalConditions": [{
                        "code": "",
                        "codeType": ""
                    }]
                },
                "existingMedicationDetails": {
                    "existingMedications": [{
                        "ndc": "",
                        "name": "",
                        "status": ""
                    }]
                },
                "pregnancyStatus": "",
                "sexAssignedAtBirth": ""
            },
            "insurances": [{
                "partnerInsuranceId": "",
                "encryptedCardholderId": "",
                "binNumber": "",
                "pcn": "",
                "groupNumber": null,
                "personCode": "",
                "relationshipCode": ""
            }],
            "residentialAddress": {
                "addressLine1": "",
                "city": "",
                "state": "",
                "zipCode": "",
                "countryCode": "US"
            }
        }, null, 2);
    }

    private handleValidation(): boolean {
        if (!this.state.program) {
            alert("Please select a program");
            return false;
        }
        if (!this.state.putPatientRequest) {
            alert("Please enter a valid patient request JSON");
            return false;
        }
        try {
            JSON.parse(this.state.putPatientRequest);
        } catch (e) {
            alert("Invalid JSON format");
            return false;
        }
        return true;
    }

    private handleSubmit() {
        if (this.handleValidation()) {
            this.setState({ formSubmitted: true });

            const apiName = "ApaqPortalAPI";
            const path = "/hubs/patient/create";

            const requestBody = {
                program: this.state.program,
                putPatientRequest: this.state.putPatientRequest
            };

            // Add these console logs
            console.log("Selected Program:", this.state.program);
            console.log("Full Request Body:", requestBody);
            console.log("putPatientRequest parsed:", JSON.parse(this.state.putPatientRequest));

            const config = {
                body: requestBody,
                headers: {}
            };

            API.post(apiName, path, config)
                .then((response) => {
                    console.log("Success response:", response);
                    this.setState({
                        resultSuccessful: true,
                        formSubmitted: false,
                        partnerPatientId: response.partnerPatientId
                    });
                })
                .catch((error) => {
                    console.error("Error details:", error);
                    console.error("Error response:", error.response);
                    console.error("Error message:", error.message);
                    console.error("Request that failed:", config.body);

                    this.setState({
                        formSubmitted: false,
                        resultSuccessful: false
                    });
                    // Show more detailed error message
                    alert(`Error creating patient: ${error.response?.data?.message || error.message || "Unknown error"}`);
                });
        }
    }

    public render() {
        const { classes } = this.props;

        return (
            <div className={classes.root}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <h1>Create HUBS Patient</h1>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl className={classes.formControl}>
                            <InputLabel>Program *</InputLabel>
                            <Select
                                value={this.state.program}
                                onChange={(e) => this.setState({ program: e.target.value as HubsProgram })}
                                required
                            >
                                {Object.values(HubsProgram).map((prog) => (
                                    <MenuItem key={prog} value={prog}>
                                        {prog}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Put Patient Request (JSON) *"
                            multiline
                            rows={50}
                            value={this.state.putPatientRequest}
                            onChange={(e) => this.setState({ putPatientRequest: e.target.value })}
                            variant="outlined"
                            className={classes.textField}
                            required
                            placeholder='Enter JSON for putPatientRequest'
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            color="default"
                            onClick={this.handleSubmit}
                        >
                            Create Patient
                        </Button>
                    </Grid>

                    {this.state.formSubmitted && (
                        <Grid item xs={12}>
                            <LoadingSpinner />
                        </Grid>
                    )}

                    {this.state.resultSuccessful && this.state.partnerPatientId && (
                        <Grid item xs={12}>
                            <div className={classes.successMessage}>
                                Partner Patient ID: {this.state.partnerPatientId}
                            </div>
                        </Grid>
                    )}
                </Grid>
            </div>
        );
    }
}

export default withStyles(styles)(CreateHubsPatientSection);
